import { useEffect, useRef, useState } from 'react';
import { useLoading } from 'react-simplikit';

import { Button } from '@/shared/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/shadcn/dialog';
import { Input } from '@/shared/components/shadcn/input';

type Props = {
  isOpen: boolean;
  onCancel: () => void;
  onSave: (title: string) => Promise<void>;
  onUnmount: () => void;
  initialTitle: string;
};

export function RenameBoardDialog({ isOpen, onCancel, onSave, onUnmount, initialTitle }: Props) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [value, setValue] = useState(initialTitle);
  const [isLoading, startLoading] = useLoading();

  useEffect(() => {
    if (!isOpen) return;
    setValue(initialTitle);
  }, [initialTitle, isOpen]);

  const handleAnimationEnd = (event: React.AnimationEvent) => {
    if (!isOpen && event.animationName === 'exit') {
      onUnmount();
    }
  };

  const handleSave = async () => {
    if (isLoading) return;

    const trimmedValue = value.trim();
    if (!trimmedValue) return;

    try {
      await startLoading(onSave(trimmedValue));
    } catch {
      return;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => onCancel()}>
      <DialogContent ref={contentRef} onAnimationEnd={handleAnimationEnd}>
        <DialogHeader>
          <DialogTitle>Rename board</DialogTitle>
        </DialogHeader>
        <Input
          value={value}
          placeholder="Enter board name"
          onChange={(e) => setValue(e.target.value)}
        />
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={value.trim().length === 0} loading={isLoading}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
